import { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  SinglePaymentsService,
  type SinglePayment
} from '../../../../services/SinglePayments';
import {
  getSinglePaymentId,
  hasSinglePaymentSession,
  resolveSinglePaymentSession
} from './singlePaymentSession';

type SinglePaymentLocationState = {
  payment?: SinglePayment;
  singlePaymentId?: string;
  token?: string;
};

type UseSinglePaymentResultState = {
  errorMessage: string | null;
  hasSinglePaymentContext: boolean;
  isLoading: boolean;
  payment: SinglePayment | null;
  singlePaymentId: string | null;
};

function getVisibleErrorMessage(error: unknown, fallbackMessage: string): string {
  const responseMessage =
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { message?: unknown } } }).response?.data?.message === 'string'
      ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
      : null;

  if (typeof responseMessage === 'string' && responseMessage.trim() !== '') {
    return responseMessage;
  }

  return fallbackMessage;
}

export function useSinglePaymentResultState(): UseSinglePaymentResultState {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigationState = (location.state ?? null) as SinglePaymentLocationState | null;
  const [payment, setPayment] = useState<SinglePayment | null>(
    navigationState?.payment ?? null
  );
  const [isLoading, setIsLoading] = useState<boolean>(!navigationState?.payment);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const singlePaymentIdFromRoute = useMemo(
    () => navigationState?.singlePaymentId ?? searchParams.get('single_payment_id'),
    [navigationState?.singlePaymentId, searchParams]
  );
  const tokenFromRoute = useMemo(
    () => navigationState?.token ?? searchParams.get('token'),
    [navigationState?.token, searchParams]
  );
  const singlePaymentId = useMemo(
    () => singlePaymentIdFromRoute ?? getSinglePaymentId(),
    [singlePaymentIdFromRoute]
  );

  const hasSinglePaymentContext = useMemo(() => {
    return Boolean(singlePaymentId || tokenFromRoute || hasSinglePaymentSession());
  }, [singlePaymentId, tokenFromRoute]);

  useEffect(() => {
    if (!hasSinglePaymentContext) {
      setIsLoading(false);
      return;
    }

    const resolvedSession = resolveSinglePaymentSession({
      singlePaymentId: singlePaymentIdFromRoute,
      token: tokenFromRoute
    });
    const resolvedSinglePaymentId = resolvedSession.singlePaymentId;
    const token = resolvedSession.token;

    if (!resolvedSinglePaymentId) {
      setErrorMessage(
        'No fue posible identificar el cobro asociado a este resultado. Abre nuevamente el enlace original para continuar.'
      );
      setIsLoading(false);
      return;
    }

    if (!token) {
      setErrorMessage(
        'No pudimos recuperar la sesión segura de este pago. Abre nuevamente el enlace original para validar el resultado.'
      );
      setIsLoading(false);
      return;
    }

    let ignore = false;

    async function loadPayment(): Promise<void> {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const response = await SinglePaymentsService.GetSinglePayment(
          token,
          resolvedSinglePaymentId
        );

        if (!ignore) {
          setPayment(response);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(
            getVisibleErrorMessage(
              error,
              'No fue posible consultar el estado actualizado del pago. Inténtalo nuevamente en unos minutos.'
            )
          );
        }
      } finally {
        if (!ignore) {
          setIsLoading(false);
        }
      }
    }

    void loadPayment();

    return () => {
      ignore = true;
    };
  }, [hasSinglePaymentContext, singlePaymentIdFromRoute, tokenFromRoute]);

  return {
    errorMessage,
    hasSinglePaymentContext,
    isLoading,
    payment,
    singlePaymentId
  };
}
